const THEME_BLOCK_PATTERN =
  /(?:\:root,\s*)?body\[data-theme='([^']+)'\]\s*\{([\s\S]*?)(?=\n\})\n\}/g;
const ROOT_BLOCK_PATTERN = /:root\s*\{([\s\S]*?)(?=\n\})\n\}/;
const CSS_VARIABLE_PATTERN = /(--[a-z0-9-]+)\s*:\s*([^;]+);/gi;
const VAR_PATTERN = /^var\((--[a-z0-9-]+)(?:,\s*([^)]+))?\)$/i;

const clampChannel = (value) => Math.min(255, Math.max(0, value));

export const parseHexColor = (color) => {
  if (typeof color !== 'string') return null;

  const normalizedColor = color.trim();
  const match = normalizedColor.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;

  const hex = match[1];
  const channels =
    hex.length === 3
      ? hex.split('').map((part) => Number.parseInt(`${part}${part}`, 16))
      : [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((part) =>
          Number.parseInt(part, 16),
        );

  return {
    r: clampChannel(channels[0]),
    g: clampChannel(channels[1]),
    b: clampChannel(channels[2]),
  };
};

const getLinearChannel = (channel) => {
  const normalized = channel / 255;
  return normalized <= 0.04045 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
};

export const getRelativeLuminance = (color) =>
  0.2126 * getLinearChannel(color.r) +
  0.7152 * getLinearChannel(color.g) +
  0.0722 * getLinearChannel(color.b);

export const getContrastRatio = (firstColor, secondColor) => {
  const firstLuminance = getRelativeLuminance(firstColor);
  const secondLuminance = getRelativeLuminance(secondColor);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

const resolveTokenValue = (themeTokens, tokenName, visitedTokens = new Set()) => {
  const rawValue = themeTokens[tokenName];
  if (!rawValue || visitedTokens.has(tokenName)) return null;

  const trimmedValue = rawValue.trim();
  const varMatch = trimmedValue.match(VAR_PATTERN);
  if (!varMatch) return trimmedValue;

  visitedTokens.add(tokenName);

  return (
    resolveTokenValue(themeTokens, varMatch[1], visitedTokens) ||
    (varMatch[2] ? varMatch[2].trim() : null)
  );
};

const parseCssVariableBlock = (blockContent) => {
  const tokens = {};
  CSS_VARIABLE_PATTERN.lastIndex = 0;
  let tokenMatch = CSS_VARIABLE_PATTERN.exec(blockContent);

  while (tokenMatch) {
    tokens[tokenMatch[1]] = tokenMatch[2].trim();
    tokenMatch = CSS_VARIABLE_PATTERN.exec(blockContent);
  }

  return tokens;
};

export const parseThemeTokensFromScss = (scss) => {
  const themes = {};
  const rootMatch = ROOT_BLOCK_PATTERN.exec(scss);
  const rootTokens = rootMatch ? parseCssVariableBlock(rootMatch[1]) : {};
  let themeMatch = THEME_BLOCK_PATTERN.exec(scss);

  while (themeMatch) {
    const [, themeName, blockContent] = themeMatch;
    const tokens = {
      ...rootTokens,
      ...(themes[themeName] || {}),
      ...parseCssVariableBlock(blockContent),
    };

    themes[themeName] = tokens;
    themeMatch = THEME_BLOCK_PATTERN.exec(scss);
  }

  return themes;
};

export const getResolvedThemeColor = (themeTokens, tokenName) => {
  const value = resolveTokenValue(themeTokens, tokenName);
  return parseHexColor(value);
};

export const getThemeTokenContrastRatio = (themeTokens, foregroundToken, backgroundToken) => {
  const foregroundColor = getResolvedThemeColor(themeTokens, foregroundToken);
  const backgroundColor = getResolvedThemeColor(themeTokens, backgroundToken);

  if (!foregroundColor || !backgroundColor) return null;

  return getContrastRatio(foregroundColor, backgroundColor);
};
